import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { Banner, Button, Card, DataTable, PageHead, StateBlock } from '@w7/ui'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import {
  formatMoney,
  parseCurrencyType,
} from './hierarchy'
import { mapHierarchyStatement } from './statementMap'
import './hierarchy.css'

export function StatementPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()

  const agent = useQuery({
    queryKey: ['cm', 'hierarchy', 'agent', id],
    queryFn: () => cmApi.hierarchyAgent(authHeaders(), id),
    enabled: Boolean(id),
  })

  const statement = useQuery({
    queryKey: ['cm', 'hierarchy', 'statement', id, agent.data?.node.currencyType],
    queryFn: async () => {
      const res = await cmApi.hierarchyStatement(authHeaders(), { id, limit: 200 })
      const currency = parseCurrencyType(agent.data?.node.currencyType)
      return mapHierarchyStatement(res.statement, currency)
    },
    enabled: Boolean(id) && Boolean(agent.data?.node),
  })

  const node = agent.data?.node
  const currency = parseCurrencyType(node?.currencyType)
  const rows = statement.data?.rows ?? []

  return (
    <div className="cm-page">
      <PageHead
        title="Account statement"
        subtitle={
          node
            ? `${node.name} · previous / next balance per movement`
            : 'Settle P&L and give/take with banking before/after'
        }
        actions={
          <Button type="button" variant="secondary" onClick={() => navigate('/hierarchy')}>
            Back to hierarchy
          </Button>
        }
      />

      {agent.isError ? (
        <StateBlock icon="error" tone="error" title="Could not load account">
          {(agent.error as Error)?.message}
        </StateBlock>
      ) : null}

      {statement.isError ? (
        <Banner tone="error" title="Statement could not be loaded">
          {(statement.error as Error)?.message}
        </Banner>
      ) : null}

      {statement.data ? (
        <Card
          title="Balances"
          subtitle={`Opening ${formatMoney(statement.data.openingBalance, currency)} → closing ${formatMoney(statement.data.closingBalance, currency)}`}
        >
          {statement.data.unresolvedCount > 0 ? (
            <Banner tone="info" title={`${statement.data.unresolvedCount} unresolved P&L leg(s)`}>
              Staged settle legs not yet applied — Remaining still includes them.
            </Banner>
          ) : null}
        </Card>
      ) : null}

      <Card title="Movements" subtitle="Newest first · previous and next balance" flush>
        <DataTable
          label="Account statement"
          rows={rows}
          rowKey={(r) => r.id}
          loading={statement.isLoading || agent.isLoading}
          skeletonRows={8}
          columns={[
            {
              id: 'at',
              header: 'Date',
              width: 170,
              cell: (r) => new Date(r.at).toLocaleString(),
              sortValue: (r) => r.at,
            },
            {
              id: 'description',
              header: 'Description',
              cell: (r) => r.description,
              sortValue: (r) => r.description,
            },
            {
              id: 'amount',
              header: 'Amount',
              numeric: true,
              cell: (r) => formatMoney(r.amount, currency),
              sortValue: (r) => r.amount,
            },
            {
              id: 'balanceBefore',
              header: 'Previous balance',
              numeric: true,
              cell: (r) =>
                r.balanceBefore == null ? '—' : formatMoney(r.balanceBefore, currency),
              sortValue: (r) => r.balanceBefore,
            },
            {
              id: 'balanceAfter',
              header: 'Next balance',
              numeric: true,
              cell: (r) => formatMoney(r.balanceAfter, currency),
              sortValue: (r) => r.balanceAfter,
            },
          ]}
          empty={
            <StateBlock icon="transfers" title="No movements yet">
              Settlements and give/take for this account will appear here with previous and next
              balance.
            </StateBlock>
          }
        />
      </Card>
    </div>
  )
}
